import React, { useEffect, useRef, useState } from "react";
import { FileText, Image as ImageIcon } from "lucide-react";
import {
  getEventFlyerStoragePath,
  getEventFlyerUrl,
  getEventMediaKind,
} from "../eventMedia";
import { loadEventFlyerBlob } from "../eventFlyerStorage";

export default function EventFlyer({
  event,
  compact = false,
  showFallback = true,
  fallbackLabel = "No flyer available",
  className = "",
  eager = false,
}) {
  const viewportTargetRef = useRef(null);
  const [imageFailed, setImageFailed] = useState(false);
  const legacyFlyerUrl = getEventFlyerUrl(event);
  const storagePath = getEventFlyerStoragePath(event);
  const rawStoragePath =
    typeof event?.flyerStoragePath === "string"
      ? event.flyerStoragePath.trim()
      : "";
  const [loadedFlyer, setLoadedFlyer] = useState({
    storagePath: "",
    url: "",
  });
  const [requestedStoragePath, setRequestedStoragePath] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(
    Boolean(rawStoragePath && !storagePath)
  );
  const mediaKind = getEventMediaKind(event);
  const title = event?.title || event?.name || "Event";
  const fileName = event?.flyerFileName || event?.fileName || "flyer attachment";
  const flyerUrl = legacyFlyerUrl || (
    loadedFlyer.storagePath === storagePath ? loadedFlyer.url : ""
  );

  useEffect(() => {
    if (
      legacyFlyerUrl ||
      !storagePath ||
      mediaKind !== "image" ||
      eager ||
      requestedStoragePath === storagePath
    ) {
      return undefined;
    }

    const target = viewportTargetRef.current;
    if (!target) return undefined;
    if (typeof window === "undefined" || typeof window.IntersectionObserver !== "function") {
      setRequestedStoragePath(storagePath);
      return undefined;
    }

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setRequestedStoragePath(storagePath);
        observer.disconnect();
      },
      { rootMargin: "240px 0px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [eager, legacyFlyerUrl, mediaKind, requestedStoragePath, storagePath]);

  useEffect(() => {
    let active = true;
    let objectUrl = "";

    setImageFailed(false);
    setPreviewFailed(false);

    if (legacyFlyerUrl) {
      setLoadedFlyer({ storagePath: "", url: "" });
      setPreviewLoading(false);
      return () => {
        active = false;
      };
    }

    setLoadedFlyer({ storagePath: "", url: "" });
    if (!storagePath) {
      setPreviewLoading(false);
      setPreviewFailed(Boolean(rawStoragePath));
      return () => {
        active = false;
      };
    }

    const loadRequested =
      requestedStoragePath === storagePath ||
      (eager && mediaKind === "image");
    if (!loadRequested) {
      setPreviewLoading(false);
      return () => {
        active = false;
      };
    }

    setPreviewLoading(true);
    loadEventFlyerBlob(storagePath)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setLoadedFlyer({ storagePath, url: objectUrl });
        setPreviewLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setPreviewFailed(true);
        setPreviewLoading(false);
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [eager, legacyFlyerUrl, mediaKind, rawStoragePath, requestedStoragePath, storagePath]);

  const frameClass = compact
    ? "h-32 max-w-xs"
    : "aspect-[4/3] w-full";

  if (previewLoading) {
    return (
      <div
        role="status"
        className={`flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-black/30 p-4 text-center text-sm text-white/70 ${frameClass} ${className}`}
      >
        <ImageIcon className="h-5 w-5" aria-hidden="true" />
        <span>Loading flyer preview…</span>
      </div>
    );
  }

  if (flyerUrl && mediaKind === "image" && !imageFailed) {
    return (
      <a
        href={flyerUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`block overflow-hidden rounded-xl border border-white/10 bg-black/30 ${frameClass} ${className}`}
        aria-label={`Open ${title} flyer`}
      >
        <img
          src={flyerUrl}
          alt={`${title} event flyer`}
          loading="lazy"
          onError={() => setImageFailed(true)}
          className="h-full w-full object-contain"
        />
      </a>
    );
  }

  if (flyerUrl) {
    return (
      <a
        href={flyerUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-black/30 p-4 text-center text-sm text-white/80 hover:bg-white/10 ${frameClass} ${className}`}
      >
        <FileText className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span>{imageFailed ? "Flyer preview unavailable — open flyer" : "Open flyer document"}</span>
      </a>
    );
  }

  if (storagePath && mediaKind === "document" && !previewFailed) {
    return (
      <button
        type="button"
        onClick={() => setRequestedStoragePath(storagePath)}
        className={`flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-black/30 p-4 text-center text-sm text-white/80 hover:bg-white/10 ${frameClass} ${className}`}
        aria-label={`Load ${title} flyer document`}
      >
        <FileText className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span>Load flyer document</span>
      </button>
    );
  }

  if (storagePath && mediaKind === "image" && !previewFailed) {
    return (
      <div
        ref={viewportTargetRef}
        className={`flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-black/30 p-4 text-center text-sm text-white/70 ${frameClass} ${className}`}
        aria-label={`${title} flyer preview`}
      >
        <ImageIcon className="h-5 w-5" aria-hidden="true" />
        <span>Flyer preview loads when visible</span>
      </div>
    );
  }

  if (previewFailed || rawStoragePath) {
    return (
      <div
        role="status"
        className={`flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-black/30 p-4 text-center text-sm text-white/80 ${frameClass} ${className}`}
      >
        <FileText className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span>{fileName}: flyer preview unavailable — review the attachment manually</span>
      </div>
    );
  }

  if (!showFallback) return null;

  return (
    <div
      className={`flex items-center justify-center gap-2 rounded-xl border border-dashed border-white/15 bg-black/20 p-4 text-center text-sm text-white/55 ${frameClass} ${className}`}
      aria-label="No event flyer available"
    >
      <ImageIcon className="h-5 w-5" aria-hidden="true" />
      <span>{fallbackLabel}</span>
    </div>
  );
}
